import type { Task } from './task'
import { asSnowflakeId, type SnowflakeId } from './snowflake'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Schedule 模块类型定义
 */

// 时间槽（用于时间轴显示）
export interface TimeSlot {
  label: string  // 显示标签，如 "9 AM", "12 PM"
  value: number  // 小时值，如 9, 12, 14
}

// Schedule 视图中的员工数据
export interface ScheduleStaff {
  id: SnowflakeId
  name: string
  initials: string
  role: string
  avatarUrl?: string
  avatarColor: string
  totalHours: number       // 当日总工时（固定 9 小时：9:00-18:00）
  workedHours: number      // 已工作时长
  isWorking: boolean       // 是否在班
  shiftStart: number       // 班次开始时间（固定 9）
  shiftEnd: number         // 班次结束时间（固定 18）
  departmentId?: SnowflakeId  // 部门ID
}

// Schedule 视图中的任务数据（从 API Task 转换）
export interface ScheduleTask {
  id: SnowflakeId
  staffId: SnowflakeId | null   // assigneeId
  title: string
  location: string            // listing.nickname 或 listing.fullAddress
  taskTypeCode: string        // 任务类型代码
  startTime: number           // 开始时间（小时浮点数，如 14.5 = 14:30）
  duration: number            // 持续时间（小时浮点数）
  plannedStartTime: number    // 计划开始时间
  plannedDuration: number     // 计划持续时间
  status: ScheduleTaskStatus
  notes?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  propertyName?: string       // listing.nickname
  description?: string
  scheduledAt?: string        // ISO date
  assigneeName?: string       // assignee.fullName
}

// 任务状态（与后端 API 一致）
export type ScheduleTaskStatus =
  | 'open'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'closed'
  | 'on_hold'

// 任务类型代码
export type ScheduleTaskTypeCode =
  | 'cleaning'
  | 'maintenance'
  | 'inspection'
  | 'guest_request'
  | 'check-in'
  | 'check-out'
  | 'other'

// 筛选状态
export interface ScheduleFilterState {
  status: ScheduleTaskStatus[]
  taskTypeCode: ScheduleTaskTypeCode[]
  assigneeId: SnowflakeId[]
  departmentId: SnowflakeId[]
  search: string
}

// 时间轴常量
export const SCHEDULE_CONSTANTS = {
  START_HOUR: 9,      // 9 AM
  END_HOUR: 18,       // 6 PM
  HOURS_COUNT: 10,    // 显示的小时数 (9 AM - 6 PM)
  ROW_HEIGHT: 76,     // 员工行高度 (px)
} as const

// 生成时间槽
export const generateTimeSlots = (): TimeSlot[] => {
  const { START_HOUR, HOURS_COUNT } = SCHEDULE_CONSTANTS
  const slots = Array.from({ length: HOURS_COUNT }, (_, i) => {
    const hour = i + START_HOUR
    let label: string
    if (hour > 12) {
      label = `${hour - 12} PM`
    } else if (hour === 12) {
      label = '12 PM'
    } else {
      label = `${hour} AM`
    }
    return { label, value: hour }
  })

  // 添加 ALL 列用于显示时间段外的任务
  slots.push({ label: 'ALL', value: -1 })

  return slots
}

// 状态样式映射
export const STATUS_STYLES: Record<ScheduleTaskStatus, string> = {
  open: 'bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800 text-purple-900',
  in_progress: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-900',
  completed: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-900',
  cancelled: 'bg-muted border-border text-foreground',
  closed: 'bg-muted border-border text-foreground',
  on_hold: 'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800 text-sky-900',
}

// 状态标签映射
export const STATUS_LABELS: Record<ScheduleTaskStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  closed: 'Closed',
  on_hold: 'On Hold',
}

// 任务类型图标颜色映射
export const TYPE_ICON_COLORS: Record<string, string> = {
  maintenance: 'text-orange-500',
  cleaning: 'text-blue-500',
  inspection: 'text-indigo-500',
  guest_request: 'text-pink-500',
  'check-in': 'text-green-500',
  'check-out': 'text-red-500',
  other: 'text-muted-foreground',
}

// 任务类型标签映射
export const TYPE_LABELS: Record<string, string> = {
  maintenance: 'Maintenance',
  cleaning: 'Cleaning',
  inspection: 'Inspection',
  guest_request: 'Guest Request',
  'check-in': 'Check-in',
  'check-out': 'Check-out',
  other: 'Other',
}

// 时间转换工具函数
export const timeUtils = {
  // ISO 时间字符串转换为小时浮点数（时区感知）
  getHoursFromISO: (isoString: string | null | undefined, timezone: string): number => {
    if (!isoString) return 0
    // 使用 dayjs 在指定时区中解析 ISO 字符串
    const d = dayjs(isoString).tz(timezone)
    return d.hour() + d.minute() / 60
  },

  // 小时浮点数转换为 ISO 时间字符串（时区感知）
  hoursToISO: (dateStr: string, hours: number, timezone: string): string => {
    // 在指定时区中解析日期字符串（YYYY-MM-DD），设置小时和分钟
    const result = dayjs.tz(dateStr, timezone)
      .hour(Math.floor(hours))
      .minute(Math.round((hours % 1) * 60))
      .second(0)
      .millisecond(0)
    return result.toISOString()
  },

  // 秒数转换为小时
  secondsToHours: (seconds: number | null | undefined): number => {
    return seconds ? seconds / 3600 : 0
  },

  // 小时转换为秒数
  hoursToSeconds: (hours: number): number => {
    return Math.round(hours * 3600)
  },

  // 格式化时间显示（如 "2:30 PM"）
  formatTimeDisplay: (hours: number): string => {
    const h = Math.floor(hours)
    const m = Math.round((hours % 1) * 60)
    const period = h >= 12 ? 'PM' : 'AM'
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h
    return m > 0 ? `${displayHour}:${m.toString().padStart(2, '0')} ${period}` : `${displayHour} ${period}`
  },
}

// 从 API Task 转换为 ScheduleTask（时区感知）
//
// 当前 attempt 的实际开始/工时直接读 task.actualStart / task.actualDurationSec
// （后端保证这两个字段代表当前 attempt）。无需再从 workSessions 聚合。
export const mapTaskToScheduleTask = (task: Task, timezone: string): ScheduleTask => {
  const startTime = timeUtils.getHoursFromISO(task.actualStart, timezone) ||
                    timeUtils.getHoursFromISO(task.plannedStartAt, timezone) ||
                    SCHEDULE_CONSTANTS.START_HOUR
  const duration = timeUtils.secondsToHours(task.actualDurationSec) ||
                   timeUtils.secondsToHours(task.plannedDurationSec) ||
                   1 // 默认 1 小时

  return {
    id: asSnowflakeId(task.id),
    staffId: task.assigneeId ? asSnowflakeId(task.assigneeId) : null,
    title: task.title,
    location: task.listing?.nickname || task.listing?.fullAddress || '',
    taskTypeCode: task.taskTypeCode || 'other',
    startTime,
    duration,
    plannedStartTime: timeUtils.getHoursFromISO(task.plannedStartAt, timezone) || SCHEDULE_CONSTANTS.START_HOUR,
    plannedDuration: timeUtils.secondsToHours(task.plannedDurationSec) || 1,
    status: task.status as ScheduleTaskStatus,
    notes: task.description,
    priority: task.priority,
    propertyName: task.listing?.nickname,
    description: task.description,
    scheduledAt: task.plannedStartAt,
    assigneeName: task.assignee?.fullName,
  }
}

// 生成员工头像颜色（基于 ID 的确定性颜色）
export const generateAvatarColor = (id: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
  ]
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

// 从名字生成首字母缩写
export const getInitials = (name: string): string => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
